import React from 'react';
import { useField } from 'formik';
import { DeliveryRateType } from '@shopify-multiseller/graphql/partner/types';
import { FormInput } from '../form';
import { SegmentedControl } from '../primitives';

interface DeliveryRateFieldsProps {
  name: string;
  /** Names the choice for assistive tech — "Domestic rate", "Zone 1 rate". */
  label: string;
  showAdditionalRate?: boolean;
}

/**
 * What a seller charges to deliver: free, or a price.
 *
 * The choice was a pair of stacked radios reading "Free Delivery" and "Fixed
 * Price". Two short, mutually exclusive options that carry no configuration of
 * their own are what the segmented control is for, and it states both at once
 * on one line instead of two.
 */
export const DeliveryRateFields: React.FC<DeliveryRateFieldsProps> = ({
  name,
  label,
  showAdditionalRate = true,
}) => {
  const [{ value: type }, , helpers] = useField<DeliveryRateType>(`${name}.type`);

  return (
    <div className="space-y-3">
      <SegmentedControl
        aria-label={label}
        size="sm"
        value={type}
        onChange={(next) => helpers.setValue(next)}
        options={[
          { value: DeliveryRateType.FreeDelivery, label: 'Free delivery' },
          { value: DeliveryRateType.FixedPrice, label: 'Fixed price' },
        ]}
      />
      {type === DeliveryRateType.FixedPrice && (
        <div className="grid gap-3 sm:grid-cols-2">
          <FormInput
            money
            name={`${name}.rate`}
            label="Rate"
            type="number"
            min={0}
            step={0.01}
            prefix="$"
          />
          {showAdditionalRate && (
            <FormInput
              money
              name={`${name}.additionalItemRate`}
              // "Additional item rate" reads as a second total. This is the
              // amount added for every item after the first, so it says so.
              label="Each extra item"
              type="number"
              min={0}
              step={0.01}
              prefix="$"
              help="Added per item after the first."
            />
          )}
        </div>
      )}
    </div>
  );
};
